import { useEffect, useRef, useState } from 'react';
import { noop } from 'ramda-adjunct';
import { TGetThumbnailLoader } from './UploadingFileThumbnail.types';
import { MediaType } from '../../../../constants/MediaType';
import { loadImage, loadVideo } from '../../../../utils/dom/media';
import { useBoolean } from '../../../../utils/hooks/useBoolean';
import { getOrientation } from '../../../../utils/uploadingFile';

const getThumbnailLoader: TGetThumbnailLoader = (mediaType) => {
  switch (mediaType) {
    case MediaType.Picture:
    case MediaType.SecurePicture:
      return loadImage;

    case MediaType.Video:
    case MediaType.SecureVideo:
      return loadVideo;

    default:
      return () => ({ promise: Promise.resolve(), abort: noop });
  }
};

export const useThumbnailLoadedAndOrientation = ({ src, file, mediaType }) => {
  const {
    value: isThumbnailLoaded,
    setFalse: setThumbnailLoadedFalse,
    setTrue: setThumbnailLoadedTrue,
  } = useBoolean(false);
  const [orientation, setOrientation] = useState(null);

  const isUnmountedForSrc = useRef({});
  const isUnmountedForFile = useRef(new Map());

  useEffect(() => {
    isUnmountedForSrc.current[src] = false;

    setThumbnailLoadedFalse();
    getThumbnailLoader(mediaType)(src)
      .promise.then(() => {
        if (isUnmountedForSrc.current[src]) {
          isUnmountedForSrc.current[src] = false;
          return;
        }
        setThumbnailLoadedTrue();
      })
      .catch(noop);

    return () => {
      isUnmountedForSrc.current[src] = true;
    };
  }, [src]);

  useEffect(() => {
    isUnmountedForFile.current.delete(file);

    setOrientation(null);
    getOrientation(file).then((orientation) => {
      if (isUnmountedForFile.current.get(file)) {
        isUnmountedForFile.current.delete(file);

        return;
      }

      setOrientation(orientation);
    });

    return () => {
      isUnmountedForFile.current.set(file, true);
    };
  }, [file]);

  return {
    isThumbnailLoaded,
    orientation,
  };
};
